import Router from 'next/router';

import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import ExternalLink from '@/components/atoms/ExternalLink';
import Heading from '@/components/atoms/Heading';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useGetMoveTokenCreate from '@/hooks/api/useGetMoveTokenCreate';
import useSetLoading from '@/hooks/useSetLoading';
import { userState } from '@/states/selectors/userState';
import { isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl } from '@/utils/constants';
import { useRecoilValue } from 'recoil';

/**
 * 移転コード発行前の同意画面。
 * 特典条件と注意事項を確認させたうえで発行 API を呼び出し、
 * 次画面で利用者が引越しコードを確認できるようにする。
 */
const MoveTokenCreate = () => {
  /** ユーザー情報 */
  const user = useRecoilValue(userState);

  const { startLoading, finishLoading } = useSetLoading();
  const { execute: getMoveTokenCreate } = useGetMoveTokenCreate();

  /**
   * 移転コード発行 API を実行し、結果に応じて確認画面またはエラー画面へ遷移する。
   */
  const onClickMoveTokenCreate = async () => {
    startLoading(ApiUrl.Write.MOVE_TOKEN_CREATE);

    // 移転トークン発行APIを実行する。
    await getMoveTokenCreate()
      .then(async () => {
        // ステータスコードが200の場合、移転(お引越し)コード確認画面へ遷移する。
        Router.push('/contract/move_token');
      })
      .catch(async (error) => {
        if (isExpiredToken(error)) {
          window.location.href = url('/login');
          sessionStorage.clear();
          return;
        }
        // ステータスコードが200以外の場合、移転(お引越し)コードエラー画面へ遷移する。
        Router.push('/contract/move_token/error');
      })
      .finally(async () => {
        finishLoading(ApiUrl.Write.MOVE_TOKEN_CREATE);
      });
  };

  // マンション契約は移転コード特典の対象外のため、この導線自体を表示しない。
  if (user.isMansion) return null;

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>ご契約情報</Heading>
        <div className='m-box-02'>
          <div className='m-box-02__inner'>
            <Heading as='h2'>移転(お引越し)の手続き</Heading>
            <Heading as='h3'>特典内容</Heading>
            <p className='m-text' data-testid='moving-text' style={{ marginBottom: '2em' }}>
              お引越し元でご利用の「NURO
              光」の解約時に発生する契約解除料・工事費残債と同額を補填いたします。
            </p>
            <Heading as='h3'>特典の受取・適用期間</Heading>
            <p className='m-text' style={{ marginBottom: '2em' }}>
              お引越し先のご契約でご登録いただいたメールアドレス宛に特典受取のご案内後、45日以内となります。
              <br />
              ※期間内にお手続きいただけなかった場合、特典をお受け取りいただけません。
            </p>
            <Heading as='h3'>お手続き方法</Heading>
            <ul className='m-text' style={{ marginBottom: '2em' }}>
              <li>
                <span>[STEP1]</span>
                <br />
                ・本注意事項にご同意の上「お引越しコード」を発行してください。発行したお引越しコードは次の画面でご確認いただけます。
              </li>
              <li>
                <span>[STEP2]</span>
                <br />
                ・お引越し先のご契約を新たにお申し込みください。お引越し専用お申し込みURLにてお引越しコードを入力の上「NURO
                光 2ギガ(3年契約/2年契約)」または「NURO 光
                10ギガ(3年契約)」にお申し込みいただくか、電話入会窓口にてお引越しコードをお伝えいただいた上で「NURO
                光 2ギガ(3年契約/2年契約)」または「NURO 光
                10ギガ(3年契約)」にお申し込みいただくことができます。
              </li>
              <li>
                <span>[STEP3]</span>
                <br />
                ・お引越し元のご契約を解約してください。解約に伴い契約解除料と工事費残債を一括で請求させていただきますが、お引越し先のご契約でお支払額と同額を補填いたします。
              </li>
              <li>
                <span>[STEP4]</span>
                <br />
                ・お引越し先のご契約で特典をお受け取りください。受取・適用条件を満たすことを確認できた月から2ヶ月後の15日までに、お引越し先のご契約でご登録いただいているメールアドレス宛に『受取手続きのご案内』のメールをお送りします。特典の受け取り手続きはメールをお送りする日の13時ごろから順次可能となります。契約解除料と工事費残債のお支払い方法やお支払い日によっては、特典をお受け取り可能となる日が遅れる場合がございます。予めご了承ください。
                <br />
                ・『受取手続きのご案内』メールのお受け取り後、受取期間内にNUROマイページより、お受け取り口座をご指定ください。
                <br />
                ・お受け取り手続き完了後、お引越し先のご契約でご登録いただいているメールアドレス宛にソニーネットワークコミュニケーションズから送金完了した旨をご連絡させていただきます。
              </li>
            </ul>
            <Heading as='h3'>キャッシュバック受取・適用条件</Heading>
            <p className='m-text'>以下の項目をすべて満たすことが条件です。</p>
            <ul className='m-text' style={{ marginBottom: '2em' }}>
              <li>
                ・本ページよりお引越しコード発行後、お引越し先の新たなご契約を、お引越し専用お申し込みURLにてお引越しコードを入力の上「NURO
                光 2ギガ(3年契約/2年契約)」または「NURO 光
                10ギガ(3年契約)」にお申し込みいただくか、電話入会窓口にてお引越しコードをお伝えいただいた上で「NURO
                光 2ギガ(3年契約/2年契約)」または「NURO 光
                10ギガ(3年契約)」にお申し込みいただいた方。
              </li>
              <li>
                ・お引越し先のNURO 光のお申し込みから12ヶ月後の末日までにお引越し先のNURO
                光をご利用開始された方。
              </li>
              <li>
                ・お引越し先のNURO
                光のご利用開始から6ヶ月後の末日までにお引越し先でのお支払い方法（決済情報）と連絡先メールアドレスのご登録を完了された方。
              </li>
              <li>
                ・お引越し先のNURO
                光のご利用開始から6か月後の末日までにお引越し元の解約を完了し、かつお引越し元のご契約の契約解除料と工事費残債の一括請求をお支払いを完了された方。
              </li>
              <li>・本特典お受け取りまでお引越し先でNURO 光を継続してご利用された方。</li>
              <li>・特典の受け取りが可能となってから45日以内にお手続きいただけた方。</li>
            </ul>
            <Heading as='h3'>注意事項等</Heading>
            <ul className='m-text' style={{ marginBottom: '2em' }}>
              <li>
                ・本特典は予告なく終了・変更させていただく場合があります。予めご了承ください。
              </li>
              <li>
                ・お引越しコードの有効期限は発行から6ヶ月間です。お引越しコードの発行後、有効期限は「お引越しコードを確認する」からご確認いただけます。
              </li>
              <li>
                ・一度ご利用になったお引越しコードは再度ご利用いただけません。再度のお申し込みが必要な場合は、新たにお引越しコードを発行してください。
              </li>
              <li>
                ・お引越しコードは「NURO
                光」のご利用が一時停止している状態では発行することができません。
              </li>
              <li>
                ・お引越しコードの発行は1契約につき5回までとなっております。6回以上の発行はできません。
              </li>
              <li>
                ・お引越しコードをご利用の上お申し込みされたお引越し先のご契約がサービス利用開始となり、お引越し元のご契約を解約された時点でお引越し手続きは完了となります。
              </li>
              <li>
                ・特典の受取手続き期間は45日間となります。期間内にお手続きいただけなかった場合無効となりますのでご注意ください。
              </li>
              <li>
                ・提供可能エリアであっても設備状況または施工上の条件等によりNURO
                光をご利用いただけない場合があります。
              </li>
              <li>
                ・お引越し先でNURO 光が開通できなかった場合、お引越し元でご利用中のNURO
                光について、契約解除料・工事費残債分のキャッシュバックは致しかねますのでご了承ください。
              </li>
              <li>
                ・お引越し元のご契約の契約解除料と工事費残債の一括請求のお支払いタイミングが異なる場合、お引越し先での特典受取のタイミングがそれぞれ異なることがございます。
              </li>
              <li>・So-netメールアドレスは継続してご利用いただけません。</li>
              <li>
                ・用紙にて口座振替登録手続を行う場合は登録完了までに1-２ヵ月要する場合があります。特典付与条件の決済登録期限を超過する可能性がある場合、マイページからの決済登録をお願いいたします。
              </li>
              <li>・以下の金融機関への振込は行えませんので、予めご了承ください。外国の銀行 等</li>
              <li>・消費税率が変更されても、補填金額の変更はございません。</li>
            </ul>

            <ExternalLink href='https://www.nuro.jp/hikari/campaign/hikkoshi/'>
              さらに詳しく
            </ExternalLink>

            <br />
            <br />
            <div className='align-center' data-testid='link-desc'>
              <span className='m-text'>
                すべての規約と注意事項をご確認の上、同意する場合は発行ボタンを押してください
              </span>
            </div>

            <div className='m-button col2'>
              <BackLinkButton href='/contract/'>戻る</BackLinkButton>
              <Button variant='primary' onClick={onClickMoveTokenCreate}>
                同意して発行する
              </Button>
            </div>
          </div>
        </div>
        <BreadcrumbLists />
      </main>
    </>
  );
};

export default MoveTokenCreate;
